import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import {
  DesignTokens,
  DEFAULT_TOKENS,
  applyTokensToDOM,
  loadFromLocalStorage,
  saveToLocalStorage,
  mergeWithDefaults,
  isValidFluidSize,
} from "@/lib/design-tokens";
import { supabase } from "@/integrations/supabase/client";

interface ThemeContextValue {
  tokens: DesignTokens;
  setTokens: (next: DesignTokens) => void;
  updateTokens: (updater: (prev: DesignTokens) => DesignTokens) => void;
  save: () => Promise<{ ok: boolean; error?: string }>;
  reset: () => void;
  exportJSON: () => string;
  importJSON: (json: string) => { ok: boolean; error?: string };
  isDirty: boolean;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initial paint: pull from localStorage to avoid FOUC, fall back to defaults.
  const [tokens, setTokensState] = useState<DesignTokens>(() => {
    const cached = loadFromLocalStorage();
    return cached ? mergeWithDefaults(cached) : DEFAULT_TOKENS;
  });
  const [savedTokens, setSavedTokens] = useState<DesignTokens>(tokens);
  const [isLoading, setIsLoading] = useState(true);

  // Apply tokens to the DOM whenever they change.
  useEffect(() => {
    applyTokensToDOM(tokens);
  }, [tokens]);

  // Hydrate from DB on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("design_tokens")
        .select("tokens")
        .eq("id", 1)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data?.tokens) {
        const merged = mergeWithDefaults(data.tokens as Partial<DesignTokens>);
        setTokensState(merged);
        setSavedTokens(merged);
        saveToLocalStorage(merged);
      } else {
        setSavedTokens((prev) => prev);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setTokens = useCallback((next: DesignTokens) => setTokensState(next), []);
  const updateTokens = useCallback(
    (updater: (prev: DesignTokens) => DesignTokens) => setTokensState((p) => updater(p)),
    []
  );

  const save = useCallback(async () => {
    // Guard: refuse to persist invalid fluid tokens (bad units, min>max, etc.)
    const invalid: string[] = [];
    for (const [key, val] of Object.entries(tokens.spacing ?? {})) {
      if (val && typeof val === "object" && "preferred" in (val as object)) {
        if (!isValidFluidSize(val as never)) invalid.push(`spacing.${key}`);
      }
    }
    if (invalid.length) {
      return {
        ok: false,
        error: `Invalid fluid token(s): ${invalid.join(", ")}. Preferred must be a unit like 10vw / 4rem.`,
      };
    }

    const { error } = await supabase
      .from("design_tokens")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .upsert([{ id: 1, tokens: tokens as any }], { onConflict: "id" });
    if (error) return { ok: false, error: error.message };
    setSavedTokens(tokens);
    saveToLocalStorage(tokens);
    return { ok: true };
  }, [tokens]);

  const reset = useCallback(() => {
    setTokensState(DEFAULT_TOKENS);
  }, []);

  const exportJSON = useCallback(() => JSON.stringify(tokens, null, 2), [tokens]);

  const importJSON = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json);
      const merged = mergeWithDefaults(parsed);
      setTokensState(merged);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }, []);

  const isDirty = useMemo(
    () => JSON.stringify(tokens) !== JSON.stringify(savedTokens),
    [tokens, savedTokens]
  );

  const value: ThemeContextValue = {
    tokens,
    setTokens,
    updateTokens,
    save,
    reset,
    exportJSON,
    importJSON,
    isDirty,
    isLoading,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
